// src/pages/Clients.js
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, Eye, Building2, Phone, Mail, MapPin, RefreshCw } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const STATUS_LABELS = { active: 'Activ', inactive: 'Inactiv', prospect: 'Prospect', archived: 'Arhivat' };
const STATUS_CLASS = { active: 'badge-success', inactive: 'badge-neutral', prospect: 'badge-info', archived: 'badge-neutral' };

function ClientModal({ client, onClose, onSave }) {
  const [form, setForm] = useState(client || { company_name: '', contact_name: '', email: '', phone: '', address: '', city: '', country: 'Romania', status: 'active', category: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.contact_name) return toast.error('Numele de contact este obligatoriu');
    setLoading(true);
    try {
      if (client?.id) { await api.put(`/clients/${client.id}`, form); toast.success('Client actualizat'); }
      else { await api.post('/clients', form); toast.success('Client creat'); }
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <span className="modal-title">{client?.id ? 'Editare Client' : 'Client Nou'}</span>
          <button className="btn-icon" onClick={onClose}><span style={{ fontSize: 18 }}>×</span></button>
        </div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Companie</label>
              <input className="form-input" value={form.company_name || ''} onChange={e => set('company_name', e.target.value)} placeholder="TechSoft SRL" />
            </div>
            <div className="form-group">
              <label className="form-label">Persoană Contact *</label>
              <input className="form-input" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Ion Popescu" />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} placeholder="contact@firma.ro" />
            </div>
            <div className="form-group">
              <label className="form-label">Telefon</label>
              <input className="form-input" value={form.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="0721000000" />
            </div>
            <div className="form-group">
              <label className="form-label">Oraș</label>
              <input className="form-input" value={form.city || ''} onChange={e => set('city', e.target.value)} placeholder="București" />
            </div>
            <div className="form-group">
              <label className="form-label">Țară</label>
              <input className="form-input" value={form.country || 'Romania'} onChange={e => set('country', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Categorie</label>
              <input className="form-input" value={form.category || ''} onChange={e => set('category', e.target.value)} placeholder="IT, Retail, etc." />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Adresă</label>
            <input className="form-input" value={form.address || ''} onChange={e => set('address', e.target.value)} placeholder="Str. Exemplu nr. 1" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Notițe</label>
            <textarea className="form-textarea" value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Informații suplimentare..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? <span className="spinner" /> : 'Salvare'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(null); // null | 'new' | client obj
  const { hasRole } = useAuth();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get('/clients', { params });
      setClients(data);
    } finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Ștergi clientul "${name}"?`)) return;
    try { await api.delete(`/clients/${id}`); toast.success('Client șters'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Eroare la ștergere'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <div className="page-title">Clienți</div>
          <div className="page-subtitle">{clients.length} clienți înregistrați</div>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={16} /> Client Nou</button>
      </div>

      <div className="filter-bar">
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Caută după nume, email, cod..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 150 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Toate statusurile</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={15} /></button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading-center"><span className="spinner" /></div>
        ) : clients.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Building2 size={48} opacity={0.3} /></div>
            <div className="empty-state-title">Niciun client găsit</div>
            <p style={{ fontSize: 13 }}>Adaugă primul client folosind butonul de mai sus.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>COD</th><th>CLIENT</th><th>CONTACT</th><th>LOCALITATE</th><th>STATUS</th><th>ACȚIUNI</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id}>
                    <td><span className="mono" style={{ color: 'var(--text-secondary)' }}>{c.code}</span></td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{c.company_name || c.contact_name}</div>
                      {c.company_name && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.contact_name}</div>}
                    </td>
                    <td>
                      {c.email && <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={12} color="var(--text-secondary)" />{c.email}</div>}
                      {c.phone && <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={12} color="var(--text-secondary)" />{c.phone}</div>}
                    </td>
                    <td>{c.city && <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}><MapPin size={12} color="var(--text-secondary)" />{c.city}</div>}</td>
                    <td><span className={`badge ${STATUS_CLASS[c.status]}`}>{STATUS_LABELS[c.status]}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-icon" title="Editare" onClick={() => setModal(c)}><Edit2 size={14} /></button>
                        {hasRole('superadmin', 'admin') && (
                          <button className="btn-icon" title="Ștergere" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(c.id, c.contact_name)}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <ClientModal
          client={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
